import React from 'react';
import { Period, PeriodValue, PERIOD_LABEL, PERIODS, todayIso, isoDate } from './period';

// The "Showing" control, shared by every monitor screen so the calendar behaves
// identically on the dashboard and on each facilitator page. It is the dropdown
// (This week / This month / All time / Custom range) plus, when "Custom range"
// is picked, two date inputs — Athira 2026-08-25 asked to be able to go back to
// a specific day's record over the 6-month programme.
//
// Fully controlled: the parent owns {period, since, until} and re-fetches on
// change. Picking "Custom range" with no dates yet seeds the last 7 days so the
// user sees a sensible window immediately instead of a blank one.
export const PeriodPicker: React.FC<{
  value: PeriodValue;
  onChange: (next: PeriodValue) => void;
  className?: string;
}> = ({ value, onChange, className }) => {
  const { period, since, until } = value;
  const today = todayIso();

  const onPeriod = (p: Period) => {
    if (p === 'custom' && !since && !until) {
      const start = new Date();
      start.setDate(start.getDate() - 6);
      onChange({ period: p, since: isoDate(start), until: today });
    } else {
      onChange({ period: p, since, until });
    }
  };

  const field =
    'rounded-lg border border-[#E9E6F4] bg-white px-2.5 py-2 text-xs font-semibold text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-200';

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className || ''}`}>
      <span className="text-xs text-slate-400">Showing</span>
      <select value={period} onChange={(e) => onPeriod(e.target.value as Period)} className={field}>
        {PERIODS.map((p) => (
          <option key={p} value={p}>{PERIOD_LABEL[p]}</option>
        ))}
      </select>
      {period === 'custom' && (
        <>
          <input
            type="date"
            aria-label="From date"
            value={since || ''}
            max={until || today}
            onChange={(e) => onChange({ period, since: e.target.value || undefined, until })}
            className={field}
          />
          <span className="text-xs text-slate-400">to</span>
          <input
            type="date"
            aria-label="To date"
            value={until || ''}
            min={since || undefined}
            max={today}
            onChange={(e) => onChange({ period, since, until: e.target.value || undefined })}
            className={field}
          />
        </>
      )}
    </div>
  );
};

export default PeriodPicker;
